/**
 * Reads parts out of a zip (every Office file is one) without the rest of it:
 * the central directory from the file's tail, then only the entries asked for,
 * by byte range. A part too large to want whole can be inflated from its start
 * and abandoned as soon as the caller has enough.
 *
 * The reads go through a RangeSource, so the same code runs against a presigned
 * URL (httpRangeSource) and against bytes in hand (bytesRangeSource, in tests).
 */

import type { RangeSource } from '@/helpers/rangeSource'

export interface ZipEntry {
  name: string
  method: number
  compressedSize: number
  uncompressedSize: number
  localHeaderOffset: number
  /** From the central directory; the local header's own may differ. */
  extraLength: number
}

export class ZipFormatError extends Error {
  override name = 'ZipFormatError'
}

const EOCD_SIGNATURE = 0x06054b50
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50
const ZIP64_EOCD_SIGNATURE = 0x06064b50
const CENTRAL_SIGNATURE = 0x02014b50
const LOCAL_SIGNATURE = 0x04034b50
const EOCD_MIN_SIZE = 22
/** The end record plus the longest comment a zip can carry. */
const EOCD_SEARCH_SIZE = EOCD_MIN_SIZE + 0xffff
const LOCAL_HEADER_SIZE = 30
/** Room for a local extra field longer than the central one says. */
const LOCAL_EXTRA_SLACK = 256
/** Entries closer than this are fetched in one request. */
const COALESCE_GAP = 32 * 1024
const METHOD_STORE = 0
const METHOD_DEFLATE = 8

const utf8 = new TextDecoder()

const view = (bytes: Uint8Array) =>
  new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

const u64 = (data: DataView, offset: number) =>
  Number(data.getBigUint64(offset, true))

const decodePartName = (name: string) =>
  name
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
    .join('/')

/**
 * Entries by name, found under either spelling of it a relationship may use.
 * A conformant package percent-encodes its zip names as it does its targets
 * (`figure%201.png`); some producers write the name literally (`figure 1.png`)
 * under the same encoded target. A part read by either keeps the name it was
 * asked for, so it repacks under the name its relationship uses.
 */
class PartIndex extends Map<string, ZipEntry> {
  private readonly decoded = new Map<string, ZipEntry>()

  override set(name: string, entry: ZipEntry) {
    super.set(name, entry)
    this.decoded.set(decodePartName(name), entry)
    return this
  }

  override get(name: string) {
    return super.get(name) ?? this.decoded.get(decodePartName(name))
  }

  override has(name: string) {
    return this.get(name) !== undefined
  }
}

export interface OpenZip {
  entries: Map<string, ZipEntry>
  /**
   * The source, with the tail already fetched for the directory served from
   * memory: a file that fits in it costs no further request.
   */
  source: RangeSource
}

/** Reads the central directory: every entry's name, size and offset. */
export const openZip = async (source: RangeSource): Promise<OpenZip> => {
  const tailStart = Math.max(0, source.size - EOCD_SEARCH_SIZE)
  const tail = await source.read(tailStart, source.size)
  const cached: RangeSource = {
    size: source.size,
    read: (start, end) =>
      start >= tailStart
        ? Promise.resolve(tail.subarray(start - tailStart, end - tailStart))
        : source.read(start, end),
  }
  return {
    entries: await readDirectory(cached, tail, tailStart),
    source: cached,
  }
}

export const readZipIndex = async (source: RangeSource) =>
  (await openZip(source)).entries

const readDirectory = async (
  source: RangeSource,
  tail: Uint8Array,
  tailStart: number,
): Promise<Map<string, ZipEntry>> => {
  const tailView = view(tail)

  let eocd = -1
  for (let i = tail.byteLength - EOCD_MIN_SIZE; i >= 0; i -= 1) {
    if (tailView.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new ZipFormatError('No end of central directory')

  let entryCount = tailView.getUint16(eocd + 10, true)
  let directorySize = tailView.getUint32(eocd + 12, true)
  let directoryOffset = tailView.getUint32(eocd + 16, true)

  if (
    entryCount === 0xffff ||
    directorySize === 0xffffffff ||
    directoryOffset === 0xffffffff
  ) {
    const locator = eocd - 20
    if (
      locator < 0 ||
      tailView.getUint32(locator, true) !== ZIP64_LOCATOR_SIGNATURE
    ) {
      throw new ZipFormatError('Zip64 locator missing')
    }
    const zip64Offset = u64(tailView, locator + 8)
    const record = view(await source.read(zip64Offset, zip64Offset + 56))
    if (record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE) {
      throw new ZipFormatError('Zip64 end record missing')
    }
    entryCount = u64(record, 32)
    directorySize = u64(record, 40)
    directoryOffset = u64(record, 48)
  }

  const directory =
    directoryOffset >= tailStart
      ? tail.subarray(
          directoryOffset - tailStart,
          directoryOffset - tailStart + directorySize,
        )
      : await source.read(directoryOffset, directoryOffset + directorySize)
  const data = view(directory)

  const entries = new PartIndex()
  let cursor = 0
  for (let i = 0; i < entryCount; i += 1) {
    if (data.getUint32(cursor, true) !== CENTRAL_SIGNATURE) {
      throw new ZipFormatError('Bad central directory entry')
    }
    const nameLength = data.getUint16(cursor + 28, true)
    const extraLength = data.getUint16(cursor + 30, true)
    const commentLength = data.getUint16(cursor + 32, true)
    let compressedSize = data.getUint32(cursor + 20, true)
    let uncompressedSize = data.getUint32(cursor + 24, true)
    let localHeaderOffset = data.getUint32(cursor + 42, true)
    const name = utf8.decode(
      directory.subarray(cursor + 46, cursor + 46 + nameLength),
    )

    // Zip64 sizes and offsets live in the extra field, in this order, each
    // present only when its 32-bit slot is saturated.
    let extra = cursor + 46 + nameLength
    const extraEnd = extra + extraLength
    while (extra + 4 <= extraEnd) {
      const id = data.getUint16(extra, true)
      const size = data.getUint16(extra + 2, true)
      if (id === 0x0001) {
        let field = extra + 4
        if (uncompressedSize === 0xffffffff) {
          uncompressedSize = u64(data, field)
          field += 8
        }
        if (compressedSize === 0xffffffff) {
          compressedSize = u64(data, field)
          field += 8
        }
        if (localHeaderOffset === 0xffffffff) {
          localHeaderOffset = u64(data, field)
        }
      }
      extra += 4 + size
    }

    entries.set(name, {
      name,
      method: data.getUint16(cursor + 10, true),
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
      extraLength,
    })
    cursor += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

const localSpanEnd = (entry: ZipEntry) =>
  entry.localHeaderOffset +
  LOCAL_HEADER_SIZE +
  entry.name.length * 4 +
  entry.extraLength +
  LOCAL_EXTRA_SLACK +
  entry.compressedSize

/** Where an entry's compressed bytes start, read from its local header. */
const dataStartIn = (bytes: Uint8Array, headerAt: number) => {
  const header = view(bytes.subarray(headerAt))
  if (header.getUint32(0, true) !== LOCAL_SIGNATURE) {
    throw new ZipFormatError('Bad local header')
  }
  return (
    headerAt +
    LOCAL_HEADER_SIZE +
    header.getUint16(26, true) +
    header.getUint16(28, true)
  )
}

const inflateRaw = async (compressed: Uint8Array): Promise<Uint8Array> => {
  const stream = new Blob([compressed as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

const decompress = (entry: ZipEntry, compressed: Uint8Array) => {
  if (entry.method === METHOD_STORE) return Promise.resolve(compressed)
  if (entry.method === METHOD_DEFLATE) return inflateRaw(compressed)
  throw new ZipFormatError(`Unsupported compression method ${entry.method}`)
}

/**
 * Reads whole entries, fetching neighbours together: Office writers lay a
 * part's relationships and the parts they name out close by, so a handful of
 * requests usually covers them all.
 */
export const readEntries = async (
  source: RangeSource,
  entries: ZipEntry[],
): Promise<Map<string, Uint8Array>> => {
  const sorted = [...entries].sort(
    (a, b) => a.localHeaderOffset - b.localHeaderOffset,
  )
  const groups: Array<{ start: number; end: number; entries: ZipEntry[] }> = []
  for (const entry of sorted) {
    const end = Math.min(source.size, localSpanEnd(entry))
    const last = groups[groups.length - 1]
    if (last && entry.localHeaderOffset - last.end <= COALESCE_GAP) {
      last.end = Math.max(last.end, end)
      last.entries.push(entry)
    } else {
      groups.push({ start: entry.localHeaderOffset, end, entries: [entry] })
    }
  }

  const result = new Map<string, Uint8Array>()
  await Promise.all(
    groups.map(async (group) => {
      const bytes = await source.read(group.start, group.end)
      for (const entry of group.entries) {
        const dataStart = dataStartIn(
          bytes,
          entry.localHeaderOffset - group.start,
        )
        const dataEnd = dataStart + entry.compressedSize
        const compressed =
          dataEnd <= bytes.byteLength
            ? bytes.subarray(dataStart, dataEnd)
            : await source.read(group.start + dataStart, group.start + dataEnd)
        result.set(entry.name, await decompress(entry, compressed))
      }
    }),
  )
  return result
}

export interface EntryHead {
  text: string
  /** The whole entry was read: `text` is all of it. */
  complete: boolean
}

/**
 * Inflates an entry from its start, a chunk of compressed bytes at a time,
 * until `enough` says the text so far will do. What is past that point is
 * never fetched.
 */
export const readEntryHead = async (
  source: RangeSource,
  entry: ZipEntry,
  enough: (text: string) => boolean,
  chunkSize = 64 * 1024,
): Promise<EntryHead> => {
  const firstEnd = Math.min(
    source.size,
    entry.localHeaderOffset +
      LOCAL_HEADER_SIZE +
      entry.name.length * 4 +
      entry.extraLength +
      LOCAL_EXTRA_SLACK +
      chunkSize,
  )
  const first = await source.read(entry.localHeaderOffset, firstEnd)
  const dataStart = entry.localHeaderOffset + dataStartIn(first, 0)
  const dataEnd = dataStart + entry.compressedSize

  const decoder = new TextDecoder()
  let text = ''

  if (entry.method === METHOD_STORE) {
    let cursor = dataStart
    while (cursor < dataEnd) {
      const next = Math.min(dataEnd, cursor + chunkSize)
      text += decoder.decode(await source.read(cursor, next), {
        stream: next < dataEnd,
      })
      cursor = next
      if (enough(text)) return { text, complete: cursor >= dataEnd }
    }
    return { text, complete: true }
  }
  if (entry.method !== METHOD_DEFLATE) {
    throw new ZipFormatError(`Unsupported compression method ${entry.method}`)
  }

  const inflater = new DecompressionStream('deflate-raw')
  const writer = inflater.writable.getWriter()
  const reader = inflater.readable.getReader()

  // Writes run ahead in their own loop: awaiting one before reading the output
  // it produced would stall on the stream's backpressure.
  let stopped = false
  const pump = (async () => {
    let cursor = dataStart
    let buffered = first.subarray(dataStart - entry.localHeaderOffset)
    try {
      while (!stopped && cursor < dataEnd) {
        const take = buffered.subarray(
          0,
          Math.min(buffered.byteLength, dataEnd - cursor),
        )
        cursor += take.byteLength
        await writer.write(take as Uint8Array<ArrayBuffer>)
        if (stopped || cursor >= dataEnd) break
        buffered = await source.read(
          cursor,
          Math.min(dataEnd, cursor + chunkSize),
        )
      }
      if (!stopped) await writer.close()
    } catch {
      // Abandoned mid-write once the reader had enough; nothing to report.
    }
  })()

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) return { text: text + decoder.decode(), complete: true }
      text += decoder.decode(value, { stream: true })
      if (enough(text)) return { text, complete: false }
    }
  } finally {
    stopped = true
    void reader.cancel().catch(() => undefined)
    void writer.abort().catch(() => undefined)
    await pump
  }
}
